/**
 * Pure geometry of the layout stage (components/layout/LayoutStage.tsx).
 *
 * The image is rendered at its natural size (`imageWidth × imageHeight` px)
 * inside a layer with `transform: translate(tx, ty) scale(zoom)` and origin
 * 0 0. `zoom` = screen px per image px. Markers are positioned in normalised
 * coordinates (0..1 of the image) and counter-scaled by `1 / zoom` so they
 * keep the same size on screen at every zoom (contract §5).
 */

import type { MarkerPoint, MarkerShape } from "../api/layouts";

export type View = { zoom: number; tx: number; ty: number };
export type Size = { width: number; height: number };
export type Point = { x: number; y: number };

/** Padding around the image when it is fitted into the stage (fraction of the stage). */
export const FIT_PADDING = 0.04;
/** How much the zoom may go below / above the fitted zoom. */
export const MIN_ZOOM_FACTOR = 0.5;
export const MAX_ZOOM_FACTOR = 8;
/** Zoom step for the +/− buttons and keys. */
export const ZOOM_STEP = 1.25;
/** Below this share of the fitted zoom the labels are hidden (except selected / hovered). */
export const LABEL_HIDE_RATIO = 0.6;

/** Zoom that shows the whole image with a little padding; 1 when the stage has no size yet. */
export function fitZoom(stage: Size, image: Size): number {
  if (stage.width <= 0 || stage.height <= 0 || image.width <= 0 || image.height <= 0) return 1;
  const usableW = stage.width * (1 - 2 * FIT_PADDING);
  const usableH = stage.height * (1 - 2 * FIT_PADDING);
  return Math.min(usableW / image.width, usableH / image.height);
}

/** The "uklopi" view: whole image visible, centred. */
export function fitView(stage: Size, image: Size): View {
  const zoom = fitZoom(stage, image);
  return {
    zoom,
    tx: (stage.width - image.width * zoom) / 2,
    ty: (stage.height - image.height * zoom) / 2,
  };
}

export function clampZoom(zoom: number, fit: number): number {
  return Math.min(Math.max(zoom, fit * MIN_ZOOM_FACTOR), fit * MAX_ZOOM_FACTOR);
}

/**
 * Zooms so that the image point under the stage point `at` stays under it
 * (wheel around the cursor, pinch around the midpoint, double-click).
 */
export function zoomAt(view: View, at: Point, nextZoom: number): View {
  const zoom = nextZoom;
  const ratio = zoom / view.zoom;
  return {
    zoom,
    tx: at.x - (at.x - view.tx) * ratio,
    ty: at.y - (at.y - view.ty) * ratio,
  };
}

export function panBy(view: View, dx: number, dy: number): View {
  return { ...view, tx: view.tx + dx, ty: view.ty + dy };
}

/**
 * Keeps the image from being dragged out of sight: at least a quarter of
 * the stage (or the whole image, when smaller) stays visible on every side.
 */
export function constrainView(view: View, stage: Size, image: Size): View {
  const w = image.width * view.zoom;
  const h = image.height * view.zoom;
  const minVisibleX = Math.min(w, stage.width / 4);
  const minVisibleY = Math.min(h, stage.height / 4);
  const tx = Math.min(Math.max(view.tx, minVisibleX - w), stage.width - minVisibleX);
  const ty = Math.min(Math.max(view.ty, minVisibleY - h), stage.height - minVisibleY);
  return { zoom: view.zoom, tx, ty };
}

/** Stage-relative screen point → normalised image coordinates (may fall outside 0..1). */
export function stageToNormalized(view: View, image: Size, p: Point): Point {
  return {
    x: (p.x - view.tx) / view.zoom / image.width,
    y: (p.y - view.ty) / view.zoom / image.height,
  };
}

/** Normalised image coordinates → stage-relative screen point. */
export function normalizedToStage(view: View, image: Size, n: Point): Point {
  return {
    x: view.tx + n.x * image.width * view.zoom,
    y: view.ty + n.y * image.height * view.zoom,
  };
}

/** View that puts the normalised point in the middle of the stage at the given zoom. */
export function centerOn(stage: Size, image: Size, n: Point, zoom: number): View {
  return {
    zoom,
    tx: stage.width / 2 - n.x * image.width * zoom,
    ty: stage.height / 2 - n.y * image.height * zoom,
  };
}

export function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

export type Box = { x: number; y: number; w: number; h: number };

/**
 * Centre of a marker in normalised coordinates: the pin's point, the centre of
 * an area's bounding box. A polygon's box is recomputed from its points, so an
 * optimistic move (before the server returns the new box) stays consistent.
 */
export function markerCenter(m: { shape: MarkerShape; x: number; y: number; w: number | null; h: number | null; points?: MarkerPoint[] | null }): Point {
  if (m.shape === "PIN") return { x: m.x, y: m.y };
  const box = m.shape === "POLYGON" && m.points && m.points.length > 0 ? polygonBounds(m.points) : { x: m.x, y: m.y, w: m.w ?? 0, h: m.h ?? 0 };
  return { x: box.x + box.w / 2, y: box.y + box.h / 2 };
}

/**
 * Circle size in normalised units (§7.1): the box is a circle in pixels when
 * `w · imageWidth = h · imageHeight`, so `h` follows from `w`.
 */
export function circleSize(wNorm: number, imageWidth: number, imageHeight: number): { w: number; h: number } {
  return { w: wNorm, h: wNorm * imageWidth / imageHeight };
}

/**
 * A circle of normalised width `w` around `center`, kept inside the image:
 * `w` is capped so the circle fits, the box is shifted rather than clipped.
 */
export function circleAround(center: Point, w: number, image: Size): Box {
  const maxW = Math.min(1, image.height / image.width);
  const size = circleSize(Math.min(w, maxW), image.width, image.height);
  return {
    x: Math.min(Math.max(0, center.x - size.w / 2), 1 - size.w),
    y: Math.min(Math.max(0, center.y - size.h / 2), 1 - size.h),
    w: size.w,
    h: size.h,
  };
}

/** Bounding box of a polygon (normalised); a zero box for no points. */
export function polygonBounds(points: MarkerPoint[]): Box {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Moves every point by (dx, dy), shortening the move so the whole polygon stays inside 0..1. */
export function translatePoints(points: MarkerPoint[], dx: number, dy: number): MarkerPoint[] {
  const b = polygonBounds(points);
  const sx = Math.min(Math.max(dx, -b.x), 1 - b.x - b.w);
  const sy = Math.min(Math.max(dy, -b.y), 1 - b.y - b.h);
  return points.map(([x, y]) => [x + sx, y + sy]);
}

/** Midpoint of every edge (edge i runs from point i to point i + 1, the last one closes the shape). */
export function edgeMidpoints(points: MarkerPoint[]): MarkerPoint[] {
  return points.map(([x, y], i) => {
    const [nx, ny] = points[(i + 1) % points.length];
    return [(x + nx) / 2, (y + ny) / 2];
  });
}

/** SVG `points` attribute for a viewBox of 0 0 1 1. */
export function svgPoints(points: MarkerPoint[]): string {
  return points.map(([x, y]) => `${x},${y}`).join(" ");
}

/** Whether a normalised point is inside the image (with a small tolerance for edge drops). */
export function insideImage(n: Point, tolerance = 0.002): boolean {
  return n.x >= -tolerance && n.x <= 1 + tolerance && n.y >= -tolerance && n.y <= 1 + tolerance;
}

export type Rect = { left: number; top: number; width: number; height: number };

/**
 * Places a popcard of `card` size next to an anchor point inside `stage`
 * (both stage-relative): to the right of the anchor, flipped to the left when
 * there is no room, then clamped so it never leaves the stage (§5).
 */
export function placeCard(anchor: Point, card: Size, stage: Size, gap = 14, margin = 8): { left: number; top: number; side: "right" | "left" } {
  let side: "right" | "left" = "right";
  let left = anchor.x + gap;
  if (left + card.width + margin > stage.width && anchor.x - gap - card.width >= margin) {
    side = "left";
    left = anchor.x - gap - card.width;
  }
  left = Math.min(Math.max(margin, left), Math.max(margin, stage.width - card.width - margin));
  let top = anchor.y - 24;
  top = Math.min(Math.max(margin, top), Math.max(margin, stage.height - card.height - margin));
  return { left, top, side };
}
